import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { NOA_LOGO_URL } from "@/components/BrandMark";

const SUGGESTIONS = [
  "How can I cut my shower water use by 30%?",
  "What's costing me the most overnight?",
  "Best thermostat schedule for my climate?",
];

export default function AIChatPanel() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm Noa, your sustainability coach. Ask me anything about cutting your home's water, energy, or carbon use." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  async function send(text) {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { message: content, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI is unavailable. Try again.");
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't reach the network. Mind asking that again?" }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="fixed bottom-6 right-6 z-30 bg-forest text-bone rounded-full shadow-lg hover:bg-forest-hover transition-all px-5 py-4 flex items-center gap-2"
          data-testid="ai-chat-fab"
        >
          <Sparkles size={18} strokeWidth={1.5} />
          <span className="text-sm font-medium">Ask Noa</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md bg-bone p-0 flex flex-col">
        <SheetHeader className="p-6 border-b border-[#E2DFD8]">
          <SheetTitle className="font-serif text-2xl text-forest flex items-center gap-2">
            <span className="w-9 h-9 relative rounded-full overflow-hidden" style={{ background: "#fff" }}>
              <img src={NOA_LOGO_URL} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 22%", transform: "scale(1.9)" }} />
            </span>
            Noa Coach
          </SheetTitle>
          <p className="text-xs text-ink/55 font-mono">NOA AI · sustainability assistant (preview)</p>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="ai-chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-forest text-bone rounded-br-md"
                    : "bg-moss/15 text-ink rounded-bl-md"
                }`}
                data-testid={`chat-msg-${m.role}`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-moss/15 rounded-2xl rounded-bl-md px-4 py-3">
                <span className="inline-flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-forest/60 animate-pulse" />
                  <span className="w-2 h-2 rounded-full bg-forest/60 animate-pulse" style={{ animationDelay: "120ms" }} />
                  <span className="w-2 h-2 rounded-full bg-forest/60 animate-pulse" style={{ animationDelay: "240ms" }} />
                </span>
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="px-6 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs bg-white border border-[#E2DFD8] rounded-full px-3 py-1.5 hover:border-forest hover:text-forest transition"
                data-testid="chat-suggestion"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="p-4 border-t border-[#E2DFD8] flex gap-2 bg-bone"
          data-testid="chat-form"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your home…"
            className="rounded-full bg-white border-[#E2DFD8] h-11"
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-full bg-forest hover:bg-forest-hover text-bone h-11 px-5 disabled:opacity-50"
            data-testid="chat-send"
          >
            <Send size={16} strokeWidth={1.5} />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
